import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import ComponentPageHeader from '../../../Common/PageHeader/ComponentPageHeader';
import DataTable from '../../../Common/DataTable';
import ComponentDeleteModal from '../../../Common/DeleteModal/ComponentDeleteModal';
import { exportToExcelData } from '../../../Common/PageHeader/helper';
import { InventoryTableColumns } from '../utils/constants';
import ComponentEquipmentIssue from './components/ComponentEquipmentIssue';
import ComponentEquipmentReturn from './components/ComponentEquipmentReturn';
import ComponentAddInventory from './components/ComponentAddInventory';
import ComponentIssueHistory from './components/ComponentIssueHistory';
import {
  getInventoryMasterStart, getInventoryStart, onInventoryDeleteStart, onReturnEquipment, setSelectedProducts, setSelectedRowIds,
} from './redux/inventorySlice';
import { RootState } from '../../../redux/rootState';
import { setLoader } from '../../../redux/loaderSlice';
import { InventoryListData } from '../utils/types';
import { formatInventoryData } from '../utils/helper';
import { getAnalystMasterStart } from '../../Job/ScreenBidWorkSheetJob/redux/bidWorksheetSlice';
import { defaultPageSize } from '../../../utils/Constant';

export default function InventoryLandingPage() {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [showEquipmentIssueModal, setShowEquipmentIssueModal] = useState(false);
  const [showEquipmentReturnModal, setShowEquipmentReturnModal] = useState(false);
  const [openInventoryForm, setOpenInventoryForm] = useState(false);
  const [openIssueHistory, setOpenIssueHistory] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  // const [selectedRowId, setSelectedRowId] = useState('');
  const [issueHistoryId, setIssueHistoryId] = useState('');
  const [productFormId, setProductFormId] = useState('');
  const [selectedEquipmentTypeValue, setSelectedEquipmentTypeValue] = useState('3');
  const [selectedInventoryValue, setSelectedInventoryValue] = useState('3');
  const dispatch = useDispatch();
  const [data, setData] = useState<InventoryListData[]>([]);

  const {
    isIssueHistoryLoading, isLoadingInventory, inventoryData, inventoryMaster, selectedRowIds, selectedProducts,
  } = useSelector((state: RootState) => state.inventoryReducer);

  const handleDelete = (id: string) => {
    setDeleteModal(true);
    setSelectedId(id);
  };

  const handleData = () => {
    const payload = {
      page,
      rowsPerPage,
      searchQuery,
      sortCol,
      sortDir,
      equipmentType: selectedEquipmentTypeValue,
      inventoryType: selectedInventoryValue,
    };
    dispatch(getInventoryStart(payload));
  };

  const handleDeleteModal = () => {
    dispatch(onInventoryDeleteStart({ productId: selectedId, callback: handleData }));
    setDeleteModal(false);
  };

  const handleEdit = (id: string) => {
    setProductFormId(id);
    setOpenInventoryForm(true);
  };

  useEffect(() => {
    if (isLoadingInventory || isIssueHistoryLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [isLoadingInventory, isIssueHistoryLoading]);

  useEffect(() => {
    handleData();
  }, [selectedEquipmentTypeValue, selectedInventoryValue, sortCol, sortDir, page, rowsPerPage, searchQuery]);

  useEffect(() => {
    dispatch(getInventoryMasterStart());
    dispatch(getAnalystMasterStart());
  }, []);

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: inventoryData?.Data?.length > 0 ? inventoryData?.Data : [],
      },
      'Inventory_list',
    );
  };

  const handleButtonClick = () => {
    setProductFormId('');
    setOpenInventoryForm(true);
  };

  const onSelectEquipmentType = (value: string) => {
    setSelectedEquipmentTypeValue(value);
  };

  const onSelectInventory = (value: string) => {
    setSelectedInventoryValue(value);
  };

  const onClickYes = () => {
    const pay = selectedProducts?.map((ele) => ({
      ProductNumber: ele.id,
      IssueId: ele.issueId,
    }));
    // const rowData = inventoryData?.Data?.find((ele: InventoryData) => ele?.ProductNumber === selectedRowId);
    setShowEquipmentReturnModal(false);
    dispatch(onReturnEquipment({ pay, callback: handleData }));
  };

  const handleIssueHistory = (id: string) => {
    setIssueHistoryId(id);
    setOpenIssueHistory(true);
  };

  const handleIssueButtonDisable = () => {
    if (selectedProducts?.length > 0) {
      const rowData = selectedProducts?.every((ele: InventoryListData) => ele?.statusId === 1);
      if (rowData) {
        return false;
      }
    }
    return true;
  };

  const handleReturnButtonDisable = () => {
    if (selectedProducts?.length > 0) {
      const rowData = selectedProducts?.every((ele: InventoryListData) => ele?.statusId === 2);
      if (rowData) {
        return false;
      }
    }
    return true;
  };

  const handleCheckBoxWithKey = (id: string) => {
    const jobIds: string[] = [...selectedRowIds];
    let products : InventoryListData[] = [...selectedProducts];
    const updatedData = data?.map((element: InventoryListData) => {
      if (element?.id === id) {
        if (!element.isChecked) {
          jobIds.push(element.id);
          products.push(element);
        } else {
          const ind = jobIds.findIndex((ele) => ele === id);
          jobIds.splice(ind, 1);
          products = products.filter((ele) => ele.id !== id);
        }
        return {
          ...element,
          isChecked: !element?.isChecked,
        };
      }
      return element;
    });

    setData(updatedData);
    dispatch(setSelectedRowIds(jobIds));
    dispatch(setSelectedProducts(products));
  };

  useEffect(() => {
    if (inventoryData?.Data?.length > 0) {
      const transData = formatInventoryData(inventoryData?.Data, selectedRowIds);
      setData(transData);
    }
  }, [inventoryData]);

  useEffect(() => () => {
    dispatch(setSelectedRowIds([]));
    dispatch(setSelectedProducts([]));
  }, []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <ComponentPageHeader subHeading="INVENTORY" downloadExcel={downloadExcel} />
        </Grid>
        <Grid item xs={12} sm={12} md={12} className="dataTableCustomMargin">
          <DataTable<InventoryListData>
            data={data}
            totalRecords={inventoryData?.TotalRecords}
            columns={InventoryTableColumns}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            searchQuery={searchQuery}
            setSearchQuery={(e) => { setSearchQuery(e); setPage(1); }}
            sortCol={sortCol}
            setSortCol={(e) => { setSortCol(e); setPage(1); }}
            sortDir={sortDir}
            setSortDir={(e) => { setSortDir(e); setPage(1); }}
            handleData={handleData}
            buttonText="Add"
            handleButtonClick={handleButtonClick}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            searchButton="Return"
            issueButton="Issue"
            // selectedRadioWithKey={selectedRowId}
            // setSelectedRadioWithKey={setSelectedRowId}
            handleIssueButton={() => setShowEquipmentIssueModal(true)}
            issueButtonDisable={handleIssueButtonDisable()}
            searchButtonDisable={handleReturnButtonDisable()}
            handleSearchButtonClick={() => setShowEquipmentReturnModal(true)}
            equipmentTypeSelectLabel="Equipment Type"
            setSelectedEquipmentType={onSelectEquipmentType}
            selectedEquipmentType={inventoryMaster?.EquipmentTypes}
            selectedEquipmentTypeValue={selectedEquipmentTypeValue}
            inventoryTypeSelectLabel="Inventory"
            setSelectedInventory={onSelectInventory}
            selectedInventory={inventoryMaster?.Status}
            selectedInventoryValue={selectedInventoryValue}
            // radioButtonColumnsWithKey={['circle']}
            checkBoxWithKey={['circle']}
            optionalCheckBox
            handleCheckBoxWithKey={handleCheckBoxWithKey}
            handleIssueHistory={handleIssueHistory}
            statusColumnsWithKey={['status']}
            handleDataFromComponent
          />
        </Grid>
      </Grid>
      <ComponentDeleteModal
        show={showDeleteModal}
        setOpenFrom={() => setDeleteModal(false)}
        handleDelete={handleDeleteModal}
      />
      <ComponentEquipmentIssue
        show={showEquipmentIssueModal}
        setOpenFrom={() => setShowEquipmentIssueModal(false)}
        products={selectedProducts}
        callback={handleData}
      />
      <ComponentEquipmentReturn
        show={showEquipmentReturnModal}
        setOpenFrom={() => setShowEquipmentReturnModal(false)}
        onClickYes={onClickYes}
        products={selectedProducts}
      />
      <ComponentAddInventory
        productFormId={productFormId}
        show={openInventoryForm}
        setOpenFrom={() => setOpenInventoryForm(false)}
        callback={handleData}
      />
      {openIssueHistory && (
      <ComponentIssueHistory
        show={openIssueHistory}
        setOpenFrom={() => setOpenIssueHistory(false)}
        rowId={issueHistoryId}
      />
      )}
    </>
  );
}
